// Polestead brand lockup (Addendum 13).
// PolesteadMark renders the Polestead mark as inline SVG, auto-selecting between
// the PRIMARY mark (>= 20px: 4 corner brackets stroke-width 6 + diamond outline +
// datum CIRCLE) and the SMALL mark (< 20px: heavier brackets stroke-width 9, NO
// diamond, datum SOLID SQUARE). Path data is verbatim from the placed brand-kit
// SVGs (public/polestead-mark.svg, public/polestead-mark-small.svg). The datum is a
// FIXED literal color — #14B6C4 on light, #6EE7F0 on dark — never currentColor and
// never token-driven, so no context can recolor the fixed point. Brackets + diamond
// follow currentColor (Ink on light).
// PolesteadLockup pairs the mark with the lowercase "polestead" wordmark
// (Inter 600, letter-spacing -0.03em, --ps-ink). Reuses the white-label
// PRODUCT_NAME only for the wordmark text so a rebrand still flows through.
import React from 'react';
import { PRODUCT_NAME } from '@/lib/brand';

export function PolesteadMark({
  size = 24,
  className,
  title = PRODUCT_NAME,
  onDark = false,
}: {
  size?: number;
  className?: string;
  title?: string;
  onDark?: boolean;
}) {
  // Datum is a fixed point: hard-coded literal, never currentColor / token.
  const datum = onDark ? '#6EE7F0' : '#14B6C4';
  const isSmall = size < 20;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={className}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {isSmall ? (
        <>
          {/* SMALL mark (< 20px): heavier brackets, no diamond, solid-square datum. */}
          <g stroke="currentColor" strokeWidth={9} strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 36V25a3 3 0 0 1 3-3h11" />
            <path d="M64 22h11a3 3 0 0 1 3 3v11" />
            <path d="M78 64v11a3 3 0 0 1-3 3H64" />
            <path d="M36 78H25a3 3 0 0 1-3-3V64" />
          </g>
          <rect x="42" y="42" width="16" height="16" fill={datum} />
        </>
      ) : (
        <>
          {/* PRIMARY mark (>= 20px): thin brackets + diamond outline + circle datum. */}
          <g stroke="currentColor" strokeWidth={6} strokeLinecap="round" strokeLinejoin="round">
            <path d="M24 38V27a3 3 0 0 1 3-3h11" />
            <path d="M62 24h11a3 3 0 0 1 3 3v11" />
            <path d="M76 62v11a3 3 0 0 1-3 3H62" />
            <path d="M38 76H27a3 3 0 0 1-3-3V62" />
            <path d="M50 40 60 50 50 60 40 50Z" strokeWidth={5} />
          </g>
          <circle cx="50" cy="50" r="3.6" fill={datum} />
        </>
      )}
    </svg>
  );
}

export function PolesteadLockup({
  markSize = 26,
  fontSize = 17,
  gap = 9,
  ink = 'var(--ps-ink, #0B1020)',
  showWordmark = true,
  onDark = false,
  stacked = false,
  className,
  style,
}: {
  markSize?: number;
  fontSize?: number;
  gap?: number;
  ink?: string;
  showWordmark?: boolean;
  onDark?: boolean;
  // stacked = mark over wordmark, centered (kit login lockup); default is the
  // horizontal header lockup.
  stacked?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'center',
        gap,
        color: ink,
        lineHeight: 1,
        ...style,
      }}
    >
      <PolesteadMark size={markSize} onDark={onDark} />
      {showWordmark && (
        <span
          style={{
            fontFamily: "var(--ps-font-sans, 'Inter', system-ui, sans-serif)",
            fontWeight: 600,
            fontSize,
            letterSpacing: '-0.03em',
            color: ink,
          }}
        >
          {PRODUCT_NAME.toLowerCase()}
        </span>
      )}
    </span>
  );
}
